import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiErrorMessage } from '../lib/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Token management
const getStoredTokens = () => {
  const accessToken = localStorage.getItem('magnolia_access_token');
  const refreshToken = localStorage.getItem('magnolia_refresh_token');
  const userStr = localStorage.getItem('magnolia_user');
  
  let user = null;
  if (userStr) {
    try {
      user = JSON.parse(userStr);
    } catch (e) {
      // Clear corrupted data
      localStorage.removeItem('magnolia_user');
    }
  }
  
  return { accessToken, refreshToken, user };
};

const storeTokens = (accessToken, refreshToken, user) => {
  localStorage.setItem('magnolia_access_token', accessToken);
  localStorage.setItem('magnolia_refresh_token', refreshToken);
  localStorage.setItem('magnolia_user', JSON.stringify(normalizeUser(user)));
};

const clearTokens = () => {
  localStorage.removeItem('magnolia_access_token');
  localStorage.removeItem('magnolia_refresh_token');
  localStorage.removeItem('magnolia_user');
};

const normalizeUser = (user) => {
  if (!user) return user;
  const roleAliases = {
    care_supervisor: 'supervisor',
    caregiver: 'care_officer'
  };
  return {
    ...user,
    role: roleAliases[user.role] || user.role,
    registered: user.registered === false ? false : true,
    kycStatus: user.kycStatus || user.kyc_status || 'not_started'
  };
};

// Axios instance with auth interceptor
const authApi = axios.create({ baseURL: API });
let refreshPromise = null;

const refreshStoredTokens = async () => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const { refreshToken } = getStoredTokens();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(`${API}/auth/refresh`, {
        refresh_token: refreshToken
      });

      const { access_token, refresh_token, user } = response.data;
      const normalizedUser = normalizeUser(user);
      storeTokens(access_token, refresh_token, normalizedUser);

      return { accessToken: access_token, refreshToken: refresh_token, user: normalizedUser };
    })().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

authApi.interceptors.request.use((config) => {
  const { accessToken } = getStoredTokens();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const { accessToken } = await refreshStoredTokens();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return authApi(originalRequest);
      } catch (refreshError) {
        clearTokens();
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state from storage
  useEffect(() => {
    const { accessToken, user: storedUser } = getStoredTokens();
    if (accessToken && storedUser) {
      setUser(normalizeUser(storedUser));
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (identifier, password) => {
    setError(null);
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, { identifier, email: identifier, password });
      const { access_token, refresh_token, user } = response.data;
      const normalizedUser = normalizeUser(user);
      
      storeTokens(access_token, refresh_token, normalizedUser);
      setUser(normalizedUser);
      setIsAuthenticated(true);
      
      return { success: true, user: normalizedUser };
    } catch (err) {
      const message = getApiErrorMessage(err, 'We could not sign you in. Please check your details and try again.');
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const { refreshToken } = getStoredTokens();
      if (refreshToken) {
        await authApi.post('/auth/logout', { refresh_token: refreshToken });
      }
    } catch (err) {
      // Ignore logout errors
    } finally {
      clearTokens();
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const forgotPassword = useCallback(async (email) => {
    setError(null);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      return { success: true };
    } catch (err) {
      // Always return success to prevent email enumeration
      return { success: true };
    }
  }, []);

  const resetPassword = useCallback(async (token, newPassword) => {
    setError(null);
    
    try {
      await axios.post(`${API}/auth/reset-password/${token}`, { 
        token,
        new_password: newPassword 
      });
      return { success: true };
    } catch (err) {
      const message = getApiErrorMessage(err, 'We could not reset your password. Please try again.');
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const setupAccount = useCallback(async (token, password) => {
    setError(null);
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/setup-account/${token}`, {
        new_password: password
      });
      
      const { access_token, refresh_token, user } = response.data;
      const normalizedUser = normalizeUser(user);
      
      storeTokens(access_token, refresh_token, normalizedUser);
      setUser(normalizedUser);
      setIsAuthenticated(true);
      
      return { success: true, user: normalizedUser };
    } catch (err) {
      const message = getApiErrorMessage(err, 'We could not complete your account setup. Please try again.');
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (payload) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/register`, payload);
      const {
        access_token,
        refresh_token,
        user,
        nextStep,
        next_step,
        requiresEmailVerification,
        requires_email_verification
      } = response.data;
      const normalizedUser = normalizeUser(user);
      const needsEmailVerification = Boolean(
        requiresEmailVerification ||
        requires_email_verification ||
        nextStep === 'verify_email' ||
        next_step === 'verify_email'
      );

      if (access_token && refresh_token && normalizedUser) {
        storeTokens(access_token, refresh_token, normalizedUser);
        setUser(normalizedUser);
        setIsAuthenticated(true);
      }

      return {
        success: true,
        data: {
          ...response.data,
          nextStep: nextStep || next_step,
          requiresEmailVerification: needsEmailVerification
        },
        user: normalizedUser
      };
    } catch (err) {
      const message = getApiErrorMessage(err, 'We could not create your account. Please check your details and try again.');
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const submitKyc = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await authApi.post('/kyc/subscriber/submit');
      const updatedUser = normalizeUser(response.data.user);

      if (updatedUser) {
        const { accessToken, refreshToken } = getStoredTokens();
        if (accessToken && refreshToken) {
          storeTokens(accessToken, refreshToken, updatedUser);
        }
        setUser(updatedUser);
      }

      return { success: true, data: response.data };
    } catch (err) {
      const message = getApiErrorMessage(err, 'We could not submit your KYC. Please check your details and try again.');
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyEmail = useCallback(async (token) => {
    setError(null);

    try {
      const response = await axios.post(`${API}/auth/verify-email/${token}`);
      const { access_token, refresh_token, user } = response.data;
      const normalizedUser = normalizeUser(user);

      if (access_token && refresh_token && normalizedUser) {
        storeTokens(access_token, refresh_token, normalizedUser);
        setUser(normalizedUser);
        setIsAuthenticated(true);
      }

      return { success: true, data: response.data, user: normalizedUser };
    } catch (err) {
      const message = getApiErrorMessage(err, 'We could not verify your email right now. Please try the link again or contact Magnolia support.');
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authApi.get('/auth/me');
      const updatedUser = normalizeUser({ ...user, ...response.data });
      setUser(updatedUser);
      localStorage.setItem('magnolia_user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (err) {
      return null;
    }
  }, [user]);

  const isFullyRegistered = useCallback(() => user?.registered === true, [user]);
  const clearError = useCallback(() => setError(null), []);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoading,
      error,
      login,
      logout,
      forgotPassword,
      resetPassword,
      setupAccount,
      register,
      submitKyc,
      verifyEmail,
      refreshUser,
      isFullyRegistered,
      authApi,
      clearError
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { authApi };
